import { useState, KeyboardEvent } from 'react';
import { uuid } from '@/lib/utils';
import { Plus, Trash2, GitBranch } from 'lucide-react';
import { useDungeonEditorStore } from '@/store/dungeonEditorStore';
import type { DungeonQuest } from '@/lib/db/dungeon-types';

interface QuestPanelProps {
  onOpenDesigner: () => void;
}

const TYPE_DOT_COLOR: Record<DungeonQuest['type'], string> = {
  main: 'bg-yellow-400',
  optional: 'bg-blue-500',
  hidden: 'bg-gray-500',
};

const labelClass = 'block text-xs text-gray-500 uppercase tracking-wide mb-1';
const inputClass = 'w-full px-2 py-1.5 bg-gray-950 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:border-gray-500';

export function QuestPanel({ onOpenDesigner }: QuestPanelProps) {
  const quests = useDungeonEditorStore((s) => s.mapData.quests);
  const selectedQuestId = useDungeonEditorStore((s) => s.selectedQuestId);
  const selectQuest = useDungeonEditorStore((s) => s.selectQuest);
  const addQuest = useDungeonEditorStore((s) => s.addQuest);
  const updateQuest = useDungeonEditorStore((s) => s.updateQuest);
  const removeQuest = useDungeonEditorStore((s) => s.removeQuest);

  const [newFlag, setNewFlag] = useState('');

  const selectedQuest = quests.find((q) => q.id === selectedQuestId) ?? null;
  const sortedQuests = [...quests].sort((a, b) => a.order - b.order);

  function handleAddQuest() {
    const id = uuid();
    const maxOrder = quests.length > 0 ? Math.max(...quests.map((q) => q.order)) : 0;
    addQuest({
      id,
      name: 'New Quest',
      description: '',
      type: 'optional',
      completionFlag: '',
      dependsOn: [],
      order: maxOrder + 1,
    });
  }

  function handleUpdate(updates: Partial<DungeonQuest>) {
    if (!selectedQuestId) return;
    updateQuest(selectedQuestId, updates);
  }

  function handleAddFlag(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return;
    const flag = newFlag.trim();
    if (!flag || !selectedQuest) return;
    if (!selectedQuest.dependsOn.includes(flag)) {
      handleUpdate({ dependsOn: [...selectedQuest.dependsOn, flag] });
    }
    setNewFlag('');
  }

  function handleRemoveFlag(flag: string) {
    if (!selectedQuest) return;
    handleUpdate({ dependsOn: selectedQuest.dependsOn.filter((f) => f !== flag) });
  }

  return (
    <div className="flex flex-col gap-0">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-200">Quests</h2>
        <div className="flex items-center gap-1">
          <button
            onClick={onOpenDesigner}
            className="p-1 rounded text-gray-400 hover:text-gray-100 hover:bg-gray-700 transition-colors"
            title="Open Quest Designer"
          >
            <GitBranch size={14} />
          </button>
          <button
            onClick={handleAddQuest}
            className="p-1 rounded text-gray-400 hover:text-gray-100 hover:bg-gray-700 transition-colors"
            title="Add Quest"
          >
            <Plus size={14} />
          </button>
        </div>
      </div>

      {/* Quest list */}
      <div className="space-y-1">
        {sortedQuests.length === 0 && (
          <div className="text-xs text-gray-600 italic">No quests yet. Click + to add one.</div>
        )}
        {sortedQuests.map((quest) => {
          const isSelected = quest.id === selectedQuestId;
          return (
            <button
              key={quest.id}
              onClick={() => selectQuest(isSelected ? null : quest.id)}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors ${
                isSelected
                  ? 'bg-blue-900/40 border border-blue-700/60'
                  : 'bg-gray-900 border border-transparent hover:border-gray-700'
              }`}
            >
              <span
                className={`flex-shrink-0 w-2 h-2 rounded-full ${TYPE_DOT_COLOR[quest.type]}`}
              />
              <span className="flex-1 text-xs text-gray-200 truncate">{quest.name || 'Unnamed'}</span>
              {quest.completionFlag && (
                <span className="text-xs text-gray-500 font-mono truncate max-w-[80px]">
                  {quest.completionFlag}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected quest editor */}
      {selectedQuest && (
        <div className="mt-4 pt-4 border-t border-gray-700 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-300 uppercase tracking-wide">
              Edit Objective
            </span>
            <button
              onClick={() => removeQuest(selectedQuest.id)}
              className="p-1 rounded text-gray-500 hover:text-red-400 hover:bg-gray-800 transition-colors"
              title="Delete quest"
            >
              <Trash2 size={13} />
            </button>
          </div>

          {/* Name */}
          <div>
            <label className={labelClass}>Name</label>
            <input
              type="text"
              className={inputClass}
              value={selectedQuest.name}
              onChange={(e) => handleUpdate({ name: e.target.value })}
            />
          </div>

          {/* Description */}
          <div>
            <label className={labelClass}>Description</label>
            <textarea
              className={`${inputClass} resize-none`}
              rows={2}
              value={selectedQuest.description}
              onChange={(e) => handleUpdate({ description: e.target.value })}
            />
          </div>

          {/* Type + Order */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelClass}>Type</label>
              <select
                className={inputClass}
                value={selectedQuest.type}
                onChange={(e) => handleUpdate({ type: e.target.value as DungeonQuest['type'] })}
              >
                <option value="main">Main</option>
                <option value="optional">Optional</option>
                <option value="hidden">Hidden</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Order</label>
              <input
                type="number"
                className={inputClass}
                value={selectedQuest.order}
                min={0}
                onChange={(e) => handleUpdate({ order: parseInt(e.target.value, 10) || 0 })}
              />
            </div>
          </div>

          {/* Completion Flag */}
          <div>
            <label className={labelClass}>Completion Flag</label>
            <input
              type="text"
              className={inputClass}
              value={selectedQuest.completionFlag}
              placeholder="rune_1_found"
              onChange={(e) => handleUpdate({ completionFlag: e.target.value })}
            />
          </div>

          {/* Depends On */}
          <div>
            <label className={labelClass}>Depends On</label>
            <div className="flex flex-wrap gap-1 mb-1.5">
              {selectedQuest.dependsOn.map((flag) => (
                <span
                  key={flag}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-800 border border-gray-700 rounded text-xs text-gray-300 font-mono"
                >
                  {flag}
                  <button
                    onClick={() => handleRemoveFlag(flag)}
                    className="text-gray-500 hover:text-red-400 transition-colors leading-none"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <input
              type="text"
              className={inputClass}
              value={newFlag}
              placeholder="Add flag + Enter"
              onChange={(e) => setNewFlag(e.target.value)}
              onKeyDown={handleAddFlag}
            />
          </div>
        </div>
      )}
    </div>
  );
}
